/**
 * The strip above the Strategies chart.
 *
 * With a large selection a row of pills grew taller than the chart, so the
 * selection lives in a dropdown: one button showing the count and a few colour
 * swatches, opening a list of the charted instrument's strategies.
 *
 * Clicking one FOCUSES it: the chart (and the orders drawer) narrow to that
 * strategy alone, and the bar shows a single focus chip. Click it again, press
 * Escape, or use the chip's × to drop focus and get the whole selection back.
 */
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  STRATEGY_SYMBOLS,
  removeSelectedStrategy,
  setFocusedStrategy,
  setStrategiesSymbol,
  setStrategyPickerOpen,
} from "@/state/slices/strategiesSlice";
import { StatusPills } from "./StatusPill";
import styles from "./Strategies.module.css";

const SWATCH_PREVIEW = 4;

export function StrategyBar() {
  const dispatch = useAppDispatch();
  const strategies = useAppSelector((s) => s.strategies.strategies);
  const selectedIds = useAppSelector((s) => s.strategies.selectedIds);
  const focusedId = useAppSelector((s) => s.strategies.focusedId);
  const symbol = useAppSelector((s) => s.strategies.symbol);
  const online = useAppSelector((s) => s.strategies.online);
  const runner = useAppSelector((s) => s.strategies.runner);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = selectedIds
    .map((id) => strategies.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s);
  // The chart shows ONE instrument, so the bar lists that instrument's
  // strategies; anything picked on the other collapses into a single chip.
  const here = selected.filter((s) => s.symbols.includes(symbol));
  const elsewhere = selected.filter((s) => !s.symbols.includes(symbol));
  const otherSymbol = elsewhere[0]?.symbols[0];
  const focused = focusedId ? selected.find((s) => s.id === focusedId) ?? null : null;

  // Close the dropdown on an outside click or Escape; Escape also drops focus.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (open) setOpen(false);
      else if (focusedId) dispatch(setFocusedStrategy(null));
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, focusedId, dispatch]);

  // The menu deliberately stays open: the chart reacts immediately, and keeping
  // the list up lets the user flip between strategies without reopening it.
  const focus = (id: string) => {
    dispatch(setFocusedStrategy(focusedId === id ? null : id));
  };

  return (
    <div className={styles.bar}>
      <button
        type="button"
        className={styles.trigger}
        data-tour="strategy-picker"
        onClick={() => dispatch(setStrategyPickerOpen(true))}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M3 17l6-6 4 4 8-8" />
          <path d="M14 7h7v7" />
        </svg>
        Strategies <span className={styles.cnt}>{selected.length}</span>
      </button>

      {selected.length === 0 ? (
        <span className={styles.barHint}>Pick strategies to see their orders on the chart</span>
      ) : (
        <div className={styles.menuWrap} ref={menuRef} data-tour="strategy-selection">
          <button
            type="button"
            className={`${styles.barPill} ${styles.menuBtn}`}
            aria-expanded={open}
            aria-haspopup="listbox"
            onClick={() => setOpen(!open)}
            disabled={here.length === 0}
            title={here.length === 0 ? `No selected strategy trades ${symbol}` : "Show the selection"}
          >
            <span className={styles.swatches} aria-hidden>
              {here.slice(0, SWATCH_PREVIEW).map((s) => (
                <i key={s.id} style={{ background: s.color }} />
              ))}
            </span>
            {here.length} on {symbol}
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {open ? (
            <div className={styles.menu} role="listbox" aria-label={`Strategies on ${symbol}`}>
              {here.map((s) => (
                <div
                  key={s.id}
                  role="option"
                  aria-selected={focusedId === s.id}
                  tabIndex={0}
                  className={`${styles.menuRow} ${focusedId === s.id ? styles.menuRowOn : ""}`}
                  onClick={() => focus(s.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      focus(s.id);
                    }
                  }}
                >
                  <span className={styles.barSw} style={{ background: s.color }} />
                  <span className={styles.menuName}>{s.name}</span>
                  <StatusPills status={s.status} compact />
                  <button
                    type="button"
                    className={styles.barX}
                    aria-label={`Remove ${s.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(removeSelectedStrategy(s.id));
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className={styles.menuFoot}>
                {focused
                  ? `Showing ${focused.name} alone — click it again to show all ${here.length}`
                  : "Click a strategy to show it alone"}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {focused && focused.symbols.includes(symbol) ? (
        <span className={`${styles.barPill} ${styles.focusPill}`} title={focused.description}>
          <span className={styles.barSw} style={{ background: focused.color }} />
          Only {focused.name}
          <StatusPills status={focused.status} compact />
          <button
            type="button"
            className={styles.barX}
            aria-label="Show all selected strategies"
            onClick={() => dispatch(setFocusedStrategy(null))}
          >
            ×
          </button>
        </span>
      ) : null}

      {elsewhere.length > 0 && otherSymbol ? (
        <button
          type="button"
          className={`${styles.barPill} ${styles.barPillElsewhere}`}
          title={`${elsewhere.map((s) => s.name).join(", ")} — click to switch to ${otherSymbol}`}
          onClick={() => dispatch(setStrategiesSymbol(otherSymbol))}
        >
          {elsewhere.length} on {otherSymbol}
        </button>
      ) : null}

      <span className={styles.barRunner} title={runner.why || undefined}>
        <span className={online ? styles.runnerLive : styles.runnerDown} />
        {online ? (runner.state === "live" ? "Live" : runner.state) : "Offline"}
      </span>
      <div className={styles.seg} role="tablist" aria-label="Chart symbol" data-tour="strategy-symbol">
        {STRATEGY_SYMBOLS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={symbol === s}
            className={symbol === s ? styles.segOn : ""}
            onClick={() => dispatch(setStrategiesSymbol(s))}
          >
            <span className={s === "XAUUSD" ? styles.icXau : styles.icBtc} />
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
